import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Calendar, MapPin, ChevronDown } from "lucide-react";
import { EVENT_CONFIG } from "@/config/eventConfig";
import { GoldButton, useDonate } from "./shared";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { openDonate } = useDonate();
  const { event, urls, images } = EVENT_CONFIG;
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const bgX = useTransform(sx, [-1, 1], [16, -16]);
  const bgY = useTransform(sy, [-1, 1], [10, -10]);
  const f1x = useTransform(sx, [-1, 1], [-24, 24]);
  const f1y = useTransform(sy, [-1, 1], [-14, 14]);
  const f2x = useTransform(sx, [-1, 1], [30, -30]);
  const f2y = useTransform(sy, [-1, 1], [20, -20]);
  const f3x = useTransform(sx, [-1, 1], [-14, 14]);
  const f3y = useTransform(sy, [-1, 1], [24, -24]);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "42%"]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMove}
      data-testid="hero-section"
      className="relative min-h-[100svh] overflow-hidden grain flex items-center"
    >
      <motion.div
        className="absolute -inset-8 bg-cover bg-center"
        style={{ backgroundImage: `url(${images.ballroom})`, x: bgX, y: bgY }}
      />
      <div className="absolute inset-0 bg-ink/80" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-wine/20 to-ink" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(230,199,134,0.12),transparent_55%)]" />

      {/* clipped photo frames — desktop collage with parallax depths */}
      <motion.div style={{ x: f1x, y: f1y }} className="hidden lg:block absolute top-[9%] right-[5%] w-64 xl:w-72 z-10">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.2, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={images.heroA}
            alt="Guests in gowns at the Styles 4 Kidz gala"
            className="w-full h-[26rem] object-cover rounded-t-[999px] rounded-b-2xl gold-stroke spotlight-card"
          />
        </motion.div>
      </motion.div>
      <motion.div style={{ x: f2x, y: f2y }} className="hidden lg:block absolute top-[44%] right-[23%] w-56 xl:w-64 z-10">
        <motion.div
          initial={{ opacity: 0, y: 44, rotate: 5 }}
          animate={{ opacity: 1, y: 0, rotate: -3 }}
          transition={{ duration: 1.1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={images.heroB}
            alt="Gala guests dressed for adult prom"
            className="w-full h-72 object-cover rounded-2xl gold-stroke spotlight-card shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
          />
        </motion.div>
      </motion.div>
      <motion.div style={{ x: f3x, y: f3y }} className="hidden lg:block absolute bottom-[5%] right-[4%] w-52 xl:w-60 z-10">
        <motion.div
          initial={{ clipPath: "inset(0 0 100% 0)" }}
          animate={{ clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.2, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={images.heroC}
            alt="Celebration on the dance floor"
            className="w-full h-64 object-cover rounded-2xl gold-stroke spotlight-card"
          />
        </motion.div>
      </motion.div>

      {/* kinetic headline */}
      <motion.div
        style={{ y: contentY, opacity: contentFade }}
        className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-24"
      >
        <div className="max-w-3xl">
          <motion.img
            src={images.logo}
            alt="Styles 4 Kidz Gala — Building Crowns 4 The Future"
            data-testid="hero-logo"
            className="w-52 sm:w-72 rounded-lg drop-shadow-[0_16px_40px_rgba(0,0,0,0.8)]"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
          />
          <motion.p
            className="mt-8 text-[10px] sm:text-xs uppercase tracking-[0.45em] text-gold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            {event.campaign}
          </motion.p>

          <h1 className="mt-5 font-serif leading-[0.95] tracking-tight">
            <MaskedLine delay={0.55} className="text-5xl sm:text-7xl xl:text-8xl text-ivory">
              CHICAGO,
            </MaskedLine>
            <MaskedLine delay={0.7} className="text-5xl sm:text-7xl xl:text-8xl text-ivory">
              PUT ON YOUR
            </MaskedLine>
            <MaskedLine delay={0.85} className="text-6xl sm:text-8xl xl:text-9xl italic gold-shimmer pb-3">
              CROWN.
            </MaskedLine>
          </h1>

          <motion.p
            className="mt-4 font-script text-3xl sm:text-4xl text-champagne"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
          >
            The Adult Prom for a Purpose
          </motion.p>

          <motion.div
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-cream text-[11px] sm:text-xs uppercase tracking-[0.2em]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            data-testid="hero-event-details"
          >
            <span className="inline-flex items-center gap-2">
              <Calendar size={13} className="text-gold" /> {event.dateDisplay}
            </span>
            <span className="hidden sm:inline text-gold/50">·</span>
            <span className="inline-flex items-center gap-2">
              <MapPin size={13} className="text-gold" /> {event.venue}, {event.city}
            </span>
            <span className="hidden sm:inline text-gold/50">·</span>
            <span>
              VIP {event.vipTime.replace(" – ", "–")} · Gala {event.galaTime.replace(" – ", "–")}
            </span>
          </motion.div>

          <motion.div
            className="mt-9 flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.45, duration: 0.8 }}
          >
            <GoldButton testid="hero-buy-tickets-button" href={urls.buyTickets} external className="pulse-gold">
              Buy Tickets
            </GoldButton>
            <GoldButton testid="hero-give-now-button" variant="outline" onClick={() => openDonate()}>
              Give Now
            </GoldButton>
            <GoldButton testid="hero-sponsor-button" variant="ghost" href="#sponsor">
              Sponsor the Future
            </GoldButton>
          </motion.div>
        </div>

        {/* mobile photo strip */}
        <motion.div
          className="lg:hidden mt-12 flex gap-3"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
        >
          {[images.heroA, images.heroB, images.heroC].map((src, i) => (
            <img
              key={src}
              src={src}
              alt="Styles 4 Kidz gala moment"
              className={`w-1/3 h-36 sm:h-44 object-cover gold-stroke ${
                i === 1 ? "rounded-2xl -translate-y-3" : "rounded-t-[999px] rounded-b-2xl"
              }`}
            />
          ))}
        </motion.div>
      </motion.div>

      <a
        href="#countdown"
        data-testid="hero-scroll-cue"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-champagne/60 hover:text-champagne transition-colors float-soft z-20"
        aria-label="Scroll down"
      >
        <ChevronDown size={26} />
      </a>
    </section>
  );
};

export default Hero;
